import { useState, useEffect, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import api, { formatApiError } from "@/lib/api";
import { useMeta } from "@/context/MetaContext";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { waLink, stageStyle, shareImages } from "@/lib/pgt";
import { useAuth } from "@/context/AuthContext";
import {
  Sparkles, Loader2, Copy, MessageCircle, Share2, CheckCircle2, XCircle,
  Lightbulb, Target, Compass, AlertTriangle, ArrowRight, Wand2, X, Palette,
  Paperclip, FileText, Mic2, Check, IndianRupee,
} from "lucide-react";
import { toast } from "sonner";

export default function CopilotPanel({ customer, onChanged, initialProductId, initialAutoGenerate }) {
  const { meta } = useMeta();
  const { user, setUser } = useAuth();
  const [stage, setStage] = useState(customer.stage);
  const [subStage, setSubStage] = useState(customer.sub_stage || "");
  const [responseStatus, setResponseStatus] = useState(customer.last_response_status || "no_response");
  const [latestMessage, setLatestMessage] = useState("");
  const [desiredAction, setDesiredAction] = useState("");
  const [language, setLanguage] = useState(customer.preferred_language || "hinglish");
  const [productIds, setProductIds] = useState(initialProductId ? [initialProductId] : []);
  const [wantShorter, setWantShorter] = useState(true);
  const [useEmoji, setUseEmoji] = useState(true);
  const [voice, setVoice] = useState(user?.tone_preset || "balanced");
  const [savingVoice, setSavingVoice] = useState(false);
  const [aiModel, setAiModel] = useState("claude");
  const [keyPoints, setKeyPoints] = useState([]);
  const [tone, setTone] = useState([]);
  const [highlight, setHighlight] = useState([]);
  const [avoid, setAvoid] = useState([]);

  const [loading, setLoading] = useState(false);
  const [draft, setDraft] = useState(null);
  const [finalMessage, setFinalMessage] = useState("");
  const [selectedVariant, setSelectedVariant] = useState("a");
  const [followUpDate, setFollowUpDate] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [catalogueId, setCatalogueId] = useState("");

  useEffect(() => {
    if (user?.tone_preset) setVoice(user.tone_preset);
  }, [user?.tone_preset]);

  const { data: products = [] } = useQuery({
    queryKey: ["products", "active"],
    queryFn: async () => (await api.get("/products", { params: { active_only: true } })).data,
  });

  const { data: catalogues = [] } = useQuery({
    queryKey: ["catalogues"],
    queryFn: async () => (await api.get("/catalogues")).data,
  });

  const stageObj = (meta?.stages || []).find((s) => s.key === stage);

  useEffect(() => {
    setSubStage((prev) => {
      if (stageObj && !stageObj.sub_stages.includes(prev)) return "";
      return prev;
    });
  }, [stage]); // eslint-disable-line

  const generate = async () => {
    setLoading(true);
    setDraft(null);
    try {
      const body = {
        customer_id: customer.id, stage, sub_stage: subStage || null,
        response_status: responseStatus, latest_message: latestMessage || null,
        desired_action: desiredAction || null, language, product_ids: productIds,
        want_shorter: wantShorter,
        use_emoji: useEmoji,
        voice_preset: voice,
        model: aiModel,
        key_points: keyPoints, tone_keywords: tone,
        highlight_keywords: highlight, avoid_keywords: avoid,
      };
      const r = await api.post("/copilot/generate", body);
      setDraft(r.data);
      setSelectedVariant("a");
      setFinalMessage(r.data.final_message || "");
      setFollowUpDate(r.data.follow_up_date || "");
      toast.success("Message drafted");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const toggleProduct = (id) =>
    setProductIds((ids) => (ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id]));

  const saveVoiceDefault = async () => {
    setSavingVoice(true);
    try {
      const r = await api.patch("/auth/me", { tone_preset: voice });
      setUser(r.data);
      toast.success("Saved as your default voice");
    } catch {
      toast.error("Could not save default voice");
    } finally {
      setSavingVoice(false);
    }
  };

  const voiceHint = (meta?.voice_presets || []).find((v) => v.value === voice)?.hint;

  const autoGenRef = useRef(false);
  useEffect(() => {
    if (autoGenRef.current || !meta || !customer) return;
    if (initialProductId || initialAutoGenerate) {
      autoGenRef.current = true;
      generate();
    }
    // eslint-disable-next-line
  }, [initialProductId, initialAutoGenerate, meta]);

  const wordCount = finalMessage.trim() ? finalMessage.trim().split(/\s+/).length : 0;

  const selectedCatalogue = catalogues.find((c) => c.id === catalogueId);
  const catalogueLink = selectedCatalogue ? `${process.env.REACT_APP_BACKEND_URL}/api/catalogues/${selectedCatalogue.id}/file` : "";
  const composedMessage = selectedCatalogue ? `${finalMessage}\n\nProduct catalogue: ${catalogueLink}` : finalMessage;

  const setStatus = async (status) => {
    if (!draft) return;
    try {
      await api.post(`/communications/${draft.id}/status`, { status, final_message: composedMessage });
    } catch (e) { /* non-blocking */ }
  };

  const copyMsg = async () => {
    try {
      await navigator.clipboard.writeText(composedMessage);
      toast.success("Message copied");
      setStatus("copied");
    } catch {
      toast.error("Copy failed — select the text manually");
    }
  };

  const openWhatsApp = () => {
    window.open(waLink(customer.wa_number, composedMessage), "_blank");
    setStatus("whatsapp_opened");
  };

  const shareProds = products.filter((p) => productIds.includes(p.id) && p.image_url);
  const hasProductImage = shareProds.length > 0;

  const shareMsg = async () => {
    if (hasProductImage) {
      const cardUrls = shareProds.map((p) => `${process.env.REACT_APP_BACKEND_URL}/api/products/${p.id}/share-card?v=${Date.now()}`);
      const r = await shareImages(cardUrls, composedMessage);
      if (r === "cancelled") return;
      setStatus("copied");
      if (r === "shared") { toast.success("Product card + message ready to send"); return; }
      if (r === "downloaded") {
        // Desktop: WhatsApp Web cannot auto-attach a file. Open the chat with the message
        // pre-filled so the user attaches the just-downloaded card (it becomes the caption).
        window.open(waLink(customer.wa_number, composedMessage), "_blank");
        setStatus("whatsapp_opened");
        toast("Card downloaded & message ready in WhatsApp — click the attach (📎) icon, pick the downloaded card, and your message becomes its caption");
      }
      return;
    }
    if (navigator.share) {
      try {
        await navigator.share({ text: composedMessage });
        setStatus("copied");
      } catch { /* cancelled */ }
    } else {
      openWhatsApp();
    }
  };

  const markNotSent = async () => {
    if (!draft) return;
    await api.post(`/communications/${draft.id}/not-sent`);
    toast("Marked as not sent");
    setDraft(null);
  };

  return (
    <div className="grid gap-5 lg:grid-cols-2">
      {/* Left: guided workflow */}
      <div className="rounded-xl border border-stone-200 bg-white p-4 sm:p-5">
        <div className="mb-4 flex items-center gap-2">
          <Compass className="h-5 w-5 text-forest" />
          <h3 className="font-heading text-lg font-semibold text-stone-900">Describe the situation</h3>
        </div>

        <div className="space-y-4">
          <Field label="1. Current stage">
            <Select value={stage} onValueChange={setStage}>
              <SelectTrigger data-testid="copilot-stage-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {(meta?.stages || []).map((s) => (
                  <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>

          <Field label="2. What happened after the last communication?">
            <Select value={subStage} onValueChange={setSubStage}>
              <SelectTrigger data-testid="copilot-substage-select"><SelectValue placeholder="Select a situation" /></SelectTrigger>
              <SelectContent>
                {(stageObj?.sub_stages || []).map((s) => (
                  <SelectItem key={s} value={s}>{s}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>

          <Field label="3. Is the customer responding?">
            <Select value={responseStatus} onValueChange={setResponseStatus}>
              <SelectTrigger data-testid="copilot-response-select"><SelectValue /></SelectTrigger>
              <SelectContent className="max-h-72">
                {(meta?.response_statuses || []).map((r) => (
                  <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>

          <Field label="4. Customer's latest message or development (optional)">
            <Textarea
              value={latestMessage}
              onChange={(e) => setLatestMessage(e.target.value)}
              placeholder="Paste the customer's exact WhatsApp reply, or a short summary"
              rows={2}
              data-testid="copilot-latest-message-input"
            />
          </Field>

          <Field label="5. What should the customer do next? (optional)">
            <Select value={desiredAction || "none"} onValueChange={(v) => setDesiredAction(v === "none" ? "" : v)}>
              <SelectTrigger data-testid="copilot-action-select"><SelectValue placeholder="Let the copilot decide" /></SelectTrigger>
              <SelectContent className="max-h-72">
                <SelectItem value="none">Let the copilot decide</SelectItem>
                {(meta?.desired_actions || []).map((a) => (
                  <SelectItem key={a} value={a}>{a}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>

          <Field label="6. Language">
            <Select value={language} onValueChange={setLanguage}>
              <SelectTrigger data-testid="copilot-language-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {(meta?.languages || []).map((l) => (
                  <SelectItem key={l.value} value={l.value}>{l.label}{l.recommended ? " ★" : ""}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>

          <Field label="Your writing voice">
            <div className="flex items-center gap-2">
              <div className="min-w-0 flex-1">
                <Select value={voice} onValueChange={setVoice}>
                  <SelectTrigger data-testid="copilot-voice-select">
                    <span className="flex items-center gap-1.5"><Mic2 className="h-3.5 w-3.5 text-gold" /><SelectValue /></span>
                  </SelectTrigger>
                  <SelectContent>
                    {(meta?.voice_presets || []).map((v) => (
                      <SelectItem key={v.value} value={v.value}>{v.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {user?.tone_preset !== voice && (
                <Button
                  type="button" variant="outline" size="sm"
                  onClick={saveVoiceDefault} disabled={savingVoice}
                  className="shrink-0 whitespace-nowrap text-xs"
                  data-testid="copilot-voice-save-default"
                >
                  {savingVoice ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Set as default"}
                </Button>
              )}
            </div>
            {voiceHint && <p className="text-[11px] text-stone-400" data-testid="copilot-voice-hint">{voiceHint}</p>}
          </Field>

          <Field label="AI model">
            <Select value={aiModel} onValueChange={setAiModel}>
              <SelectTrigger data-testid="copilot-model-select">
                <span className="flex items-center gap-1.5"><Sparkles className="h-3.5 w-3.5 text-gold" /><SelectValue /></span>
              </SelectTrigger>
              <SelectContent>
                {(meta?.ai_models || []).map((m) => (
                  <SelectItem key={m.key} value={m.key} data-testid={`copilot-model-${m.key}`}>{m.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-[11px] text-stone-400">Choose which AI writes the draft — try Claude, ChatGPT or Gemini and keep the wording you like best.</p>
          </Field>

          {products.length > 0 && (
            <Field label="7. Relevant products (optional)">
              <div className="flex flex-wrap gap-2">
                {products.map((p) => (
                  <button
                    key={p.id}
                    onClick={() => toggleProduct(p.id)}
                    data-testid={`copilot-product-${p.id}`}
                    className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
                      productIds.includes(p.id)
                        ? "border-forest bg-forest text-ivory"
                        : "border-stone-200 bg-white text-stone-600 hover:border-forest/40"
                    }`}
                  >
                    {p.name}
                  </button>
                ))}
              </div>
            </Field>
          )}

          <div className="rounded-lg border border-dashed border-stone-300 bg-stone-50/50 p-3">
            <div className="mb-2 flex items-center gap-1.5 text-xs font-semibold text-stone-600">
              <Wand2 className="h-3.5 w-3.5 text-gold" /> Guide the message with keywords (optional)
            </div>
            <p className="mb-2.5 text-[11px] text-stone-400">Type a keyword and press Enter. These steer the tone and content — the copilot still won't state any unverified price, stock or delivery claim.</p>
            <div className="space-y-2.5">
              <KeywordField label="Key points to mention" value={keyPoints} onChange={setKeyPoints} placeholder="e.g. Assam CTC, fresh lot" testId="copilot-key-points" />
              <KeywordField label="Tone & style" value={tone} onChange={setTone} placeholder="e.g. warm, brief, respectful" testId="copilot-tone" />
              <KeywordField label="Offer or highlight" value={highlight} onChange={setHighlight} placeholder="e.g. new arrival, single garden" testId="copilot-highlight" />
              <KeywordField label="Avoid mentioning" value={avoid} onChange={setAvoid} placeholder="e.g. discount, urgency" testId="copilot-avoid" />
            </div>
          </div>

          <div className="flex items-center justify-between rounded-lg bg-stone-50 px-3 py-2">
            <span className="text-sm text-stone-600">Also generate a shorter version</span>
            <Switch checked={wantShorter} onCheckedChange={setWantShorter} data-testid="copilot-shorter-toggle" />
          </div>

          <div className="flex items-center justify-between rounded-lg bg-stone-50 px-3 py-2">
            <span className="text-sm text-stone-600">Include emoji <span className="text-xs text-stone-400">(off for very formal customers)</span></span>
            <Switch checked={useEmoji} onCheckedChange={setUseEmoji} data-testid="copilot-emoji-toggle" />
          </div>

          <Button
            onClick={generate}
            disabled={loading}
            className="w-full bg-gold hover:bg-gold-bright text-white h-11"
            data-testid="copilot-generate-button"
          >
            {loading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Thinking…</> : <><Sparkles className="mr-2 h-4 w-4" /> {draft ? "Regenerate message" : "Generate guidance & message"}</>}
          </Button>
        </div>
      </div>

      {/* Right: AI output */}
      <div className="space-y-4">
        {!draft && !loading && (
          <div className="grid place-items-center rounded-xl border border-dashed border-stone-300 bg-white/40 py-20 text-center">
            <div>
              <Sparkles className="mx-auto h-9 w-9 text-stone-300" />
              <p className="mt-3 max-w-xs text-sm text-stone-400">
                Fill in the situation and generate. The copilot will explain the objective and draft a WhatsApp message.
              </p>
            </div>
          </div>
        )}
        {loading && (
          <div className="grid place-items-center rounded-xl border border-stone-200 bg-white py-20">
            <Loader2 className="h-7 w-7 animate-spin text-gold" />
          </div>
        )}

        {draft && (
          <div className="animate-fade-up space-y-4">
            {draft.recommended_tone && (
              <div className="flex items-center gap-2 rounded-lg border border-gold/30 bg-gold-bg/50 px-3 py-2" data-testid="ai-tone">
                <Palette className="h-4 w-4 shrink-0 text-gold" />
                <span className="text-xs text-stone-600">
                  <span className="font-semibold text-gold">Tone:</span> {draft.recommended_tone}
                  {draft.tone_scenario ? ` · ${draft.tone_scenario}` : ""}
                </span>
              </div>
            )}
            <GuidanceCard icon={Lightbulb} title="Situation identified" tone="bg-blue-50 text-blue-700" text={draft.situation_identified} testId="ai-situation" />
            <GuidanceCard icon={Target} title="Communication objective" tone="bg-forest-light text-forest" text={draft.communication_objective} testId="ai-objective" />
            <GuidanceCard icon={Compass} title="Recommended approach" tone="bg-gold-bg text-gold" text={draft.recommended_approach} testId="ai-approach" />

            {draft.should_pause_or_close && (
              <div className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700" data-testid="ai-pause-warning">
                <div className="flex items-center gap-2 font-semibold"><AlertTriangle className="h-4 w-4" /> Consider pausing</div>
                <p className="mt-1">This customer has reached the follow-up limit. A respectful close-the-loop message is drafted below. Consider moving them to Dormant / archiving.</p>
              </div>
            )}

            {draft.quality_warnings?.length > 0 && (
              <div className="rounded-xl border border-amber-200 bg-amber-50 p-3" data-testid="ai-warnings">
                <div className="flex items-center gap-2 text-sm font-semibold text-amber-800"><AlertTriangle className="h-4 w-4" /> Quality check</div>
                <ul className="mt-1 list-disc pl-5 text-xs text-amber-700">
                  {draft.quality_warnings.map((w, i) => <li key={i}>{w}</li>)}
                </ul>
              </div>
            )}

            {/* A/B options */}
            {draft.alternative_message && (
              <div className="rounded-xl border border-stone-200 bg-white p-3" data-testid="ai-variants">
                <div className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-stone-500">
                  <Sparkles className="h-3.5 w-3.5 text-gold" /> Choose an option
                </div>
                <div className="grid gap-2 sm:grid-cols-2">
                  <VariantCard
                    label="Option A" active={selectedVariant === "a"} text={draft.ai_draft}
                    onUse={() => { setFinalMessage(draft.ai_draft || ""); setSelectedVariant("a"); }}
                    testId="variant-a"
                  />
                  <VariantCard
                    label="Option B" active={selectedVariant === "b"} text={draft.alternative_message}
                    onUse={() => { setFinalMessage(draft.alternative_message || ""); setSelectedVariant("b"); }}
                    testId="variant-b"
                  />
                </div>
                <p className="mt-2 text-[11px] text-stone-400">Tap the one you prefer — it loads into the editable message below, where you can fine-tune it.</p>
              </div>
            )}

            {/* Editable WhatsApp message */}
            <div className="rounded-xl border-2 border-forest/20 bg-white p-4">
              <div className="mb-2 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <MessageCircle className="h-4 w-4 text-whatsapp-dark" />
                  <span className="font-heading font-semibold text-stone-900">WhatsApp message</span>
                </div>
                <span className="text-xs font-medium text-stone-400">{wordCount} words</span>
              </div>
              <Textarea
                value={finalMessage}
                onChange={(e) => setFinalMessage(e.target.value)}
                rows={6}
                className="bg-whatsapp-bg/40 border-stone-200 text-stone-800 leading-relaxed"
                data-testid="ai-message-editable"
              />
              {draft.shorter_version && (
                <button
                  onClick={() => { setFinalMessage(draft.shorter_version); toast("Using shorter version"); }}
                  className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-forest hover:underline"
                  data-testid="use-shorter-version"
                >
                  <Wand2 className="h-3 w-3" /> Use shorter version
                </button>
              )}

              {/* Attach catalogue */}
              {catalogues.length > 0 && (
                <div className="mt-3 rounded-lg border border-stone-200 bg-stone-50/60 p-2.5">
                  <div className="flex items-center gap-1.5 text-xs font-semibold text-stone-600">
                    <Paperclip className="h-3.5 w-3.5 text-forest" /> Attach catalogue (optional)
                  </div>
                  <Select value={catalogueId || "__none__"} onValueChange={(v) => setCatalogueId(v === "__none__" ? "" : v)}>
                    <SelectTrigger className="mt-2 h-9 bg-white" data-testid="copilot-catalogue-select"><SelectValue placeholder="No catalogue" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">No catalogue</SelectItem>
                      {catalogues.map((c) => <SelectItem key={c.id} value={c.id}>{c.title}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  {selectedCatalogue && (
                    <p className="mt-1.5 flex items-center gap-1 text-[11px] text-forest" data-testid="copilot-catalogue-attached">
                      <FileText className="h-3 w-3" /> Catalogue link will be added to the message.
                    </p>
                  )}
                </div>
              )}

              {/* Action bar */}
              <div className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-3">
                <Button variant="outline" onClick={copyMsg} data-testid="whatsapp-copy-button"><Copy className="mr-1.5 h-4 w-4" /> Copy</Button>
                {hasProductImage ? (
                  <>
                    <Button className="bg-whatsapp hover:bg-whatsapp/90 text-white" onClick={shareMsg} data-testid="whatsapp-share-button"><Share2 className="mr-1.5 h-4 w-4" /> Send card</Button>
                    <Button variant="outline" onClick={openWhatsApp} data-testid="whatsapp-open-link"><MessageCircle className="mr-1.5 h-4 w-4" /> Text only</Button>
                  </>
                ) : (
                  <>
                    <Button className="bg-whatsapp hover:bg-whatsapp/90 text-white" onClick={openWhatsApp} data-testid="whatsapp-open-link"><MessageCircle className="mr-1.5 h-4 w-4" /> WhatsApp</Button>
                    <Button variant="outline" onClick={shareMsg} data-testid="whatsapp-share-button"><Share2 className="mr-1.5 h-4 w-4" /> Share</Button>
                  </>
                )}
                <Button className="col-span-2 sm:col-span-2 bg-forest hover:bg-forest-600 text-ivory" onClick={() => setConfirmOpen(true)} data-testid="whatsapp-confirm-sent-button"><CheckCircle2 className="mr-1.5 h-4 w-4" /> Confirm Sent Manually</Button>
                <Button variant="ghost" className="text-stone-500" onClick={markNotSent} data-testid="whatsapp-mark-not-sent-button"><XCircle className="mr-1.5 h-4 w-4" /> Not sent</Button>
              </div>
              <p className="mt-2 text-[11px] text-stone-400">
                {hasProductImage
                  ? "Phone: tap \u201cSend card\u201d \u2192 choose WhatsApp; the card attaches with your message as its caption. Computer: the card downloads and WhatsApp opens with your message \u2014 click \ud83d\udcce and attach the downloaded card. Opening WhatsApp does not send it \u2014 confirm only after you actually send."
                  : "Opening WhatsApp does not send the message. Only confirm after you have actually sent it."}
              </p>
            </div>

            <div className="rounded-xl border border-stone-200 bg-white p-4">
              <div className="flex items-center gap-2 text-sm font-semibold text-stone-800"><ArrowRight className="h-4 w-4 text-forest" /> Next step</div>
              <p className="mt-1 text-sm text-stone-600" data-testid="ai-next-action">{draft.next_action}</p>
              {draft.trigger_condition && <p className="mt-1 text-xs text-stone-400">Trigger: {draft.trigger_condition}</p>}
              <div className="mt-3">
                <Label className="text-xs font-semibold text-stone-500">Follow-up date <span className="font-normal text-stone-400">(AI suggested — edit if needed)</span></Label>
                <Input type="date" value={followUpDate} onChange={(e) => setFollowUpDate(e.target.value)} className="mt-1 w-full sm:w-52" data-testid="ai-followup-date" />
              </div>
            </div>
          </div>
        )}
      </div>

      {draft && (
        <ConfirmSentDialog
          open={confirmOpen}
          onOpenChange={setConfirmOpen}
          draft={draft}
          finalMessage={composedMessage}
          productIds={productIds}
          customer={customer}
          meta={meta}
          followUp={followUpDate}
          setFollowUp={setFollowUpDate}
          onDone={() => { setConfirmOpen(false); setDraft(null); onChanged?.(); }}
        />
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs font-semibold text-stone-500">{label}</Label>
      {children}
    </div>
  );
}

function KeywordField({ label, value, onChange, placeholder, testId }) {
  const [text, setText] = useState("");
  const add = (t) => {
    const v = (t || "").trim().replace(/,$/, "").trim();
    if (v && !value.includes(v)) onChange([...value, v]);
    setText("");
  };
  const onKey = (e) => {
    if (e.key === "Enter" || e.key === ",") { e.preventDefault(); add(text); }
    else if (e.key === "Backspace" && !text && value.length) onChange(value.slice(0, -1));
  };
  return (
    <div className="space-y-1">
      <Label className="text-[11px] font-medium text-stone-500">{label}</Label>
      <div className="flex flex-wrap items-center gap-1.5 rounded-md border border-input bg-white px-2 py-1.5 focus-within:ring-1 focus-within:ring-forest">
        {value.map((t, i) => (
          <span key={i} className="inline-flex items-center gap-1 rounded-full bg-forest/10 px-2 py-0.5 text-xs font-medium text-forest">
            {t}
            <button type="button" onClick={() => onChange(value.filter((_, j) => j !== i))} data-testid={`${testId}-remove-${i}`}>
              <X className="h-3 w-3" />
            </button>
          </span>
        ))}
        <input
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={onKey}
          onBlur={() => add(text)}
          placeholder={value.length ? "" : placeholder}
          className="min-w-[110px] flex-1 bg-transparent text-sm outline-none placeholder:text-stone-400"
          data-testid={testId}
        />
      </div>
    </div>
  );
}

function VariantCard({ label, text, active, onUse, testId }) {
  return (
    <button
      type="button" onClick={onUse} data-testid={testId}
      className={`text-left rounded-lg border p-3 transition-colors ${
        active
          ? "border-forest bg-forest-light/40 ring-1 ring-forest/30"
          : "border-stone-200 bg-white hover:border-forest/40"
      }`}
    >
      <div className="mb-1.5 flex items-center justify-between">
        <span className={`text-xs font-semibold ${active ? "text-forest" : "text-stone-500"}`}>{label}</span>
        {active && <span className="inline-flex items-center gap-1 text-[11px] font-medium text-forest"><Check className="h-3 w-3" /> In use</span>}
      </div>
      <p className="max-h-40 overflow-auto whitespace-pre-wrap text-xs leading-relaxed text-stone-600">{text}</p>
    </button>
  );
}

function GuidanceCard({ icon: Icon, title, tone, text, testId }) {  return (
    <div className="rounded-xl border border-stone-200 bg-white p-3.5">
      <div className="flex items-center gap-2">
        <span className={`inline-flex h-6 w-6 items-center justify-center rounded-md ${tone}`}><Icon className="h-3.5 w-3.5" /></span>
        <span className="text-xs font-semibold uppercase tracking-wide text-stone-500">{title}</span>
      </div>
      <p className="mt-2 text-sm text-stone-700 leading-relaxed" data-testid={testId}>{text}</p>
    </div>
  );
}

function ConfirmSentDialog({ open, onOpenChange, draft, finalMessage, productIds, customer, meta, followUp, setFollowUp, onDone }) {
  const [nextAction, setNextAction] = useState(draft.next_action || "");
  const [changeStage, setChangeStage] = useState(!!draft.suggested_next_stage && draft.suggested_next_stage !== customer.stage);
  const [newStage, setNewStage] = useState(draft.suggested_next_stage || customer.stage);
  const [newSub, setNewSub] = useState("");
  const [orderValue, setOrderValue] = useState("");
  const [loading, setLoading] = useState(false);

  const stageObj = (meta?.stages || []).find((s) => s.key === newStage);
  const isOrder = changeStage && newStage === "stage_4_order";

  const confirm = async () => {
    setLoading(true);
    try {
      await api.post(`/communications/${draft.id}/confirm-sent`, {
        final_message: finalMessage,
        next_action: nextAction,
        follow_up_date: followUp || null,
        change_stage: changeStage,
        new_stage: changeStage ? newStage : null,
        new_sub_stage: changeStage ? (newSub || null) : null,
        shared_product_ids: productIds,
        order_value: isOrder && orderValue ? Number(orderValue) : null,
      });
      toast.success("Recorded as sent. This is now the customer's last communication.");
      onDone();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-heading">Confirm message sent</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="rounded-lg bg-whatsapp-bg/40 p-3 text-sm text-stone-700 max-h-32 overflow-auto whitespace-pre-wrap">{finalMessage}</div>
          <div className="space-y-1.5">
            <Label>Next action</Label>
            <Input value={nextAction} onChange={(e) => setNextAction(e.target.value)} data-testid="confirm-next-action" />
          </div>
          <div className="space-y-1.5">
            <Label>Next follow-up date</Label>
            <Input type="date" value={followUp} onChange={(e) => setFollowUp(e.target.value)} data-testid="confirm-followup-date" />
          </div>
          <div className="flex items-center justify-between rounded-lg bg-stone-50 px-3 py-2">
            <span className="text-sm text-stone-600">Advance the sales stage</span>
            <Switch checked={changeStage} onCheckedChange={setChangeStage} data-testid="confirm-change-stage" />
          </div>
          {changeStage && (
            <div className="grid gap-2">
              <Select value={newStage} onValueChange={setNewStage}>
                <SelectTrigger data-testid="confirm-new-stage"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {(meta?.stages || []).map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={newSub} onValueChange={setNewSub}>
                <SelectTrigger data-testid="confirm-new-substage"><SelectValue placeholder="Sub-stage (optional)" /></SelectTrigger>
                <SelectContent>
                  {(stageObj?.sub_stages || []).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          {isOrder && (
            <div className="space-y-1.5 rounded-lg border border-gold/30 bg-gold-bg/40 p-3" data-testid="confirm-order-value-wrap">
              <Label className="flex items-center gap-1.5 text-forest"><IndianRupee className="h-3.5 w-3.5 text-gold" /> Order value (₹)</Label>
              <Input type="number" min="0" step="1" value={orderValue} onChange={(e) => setOrderValue(e.target.value)} placeholder="e.g. 45000" data-testid="confirm-order-value" />
              <p className="text-[11px] text-stone-500">You're marking this order confirmed — enter the sales value so it counts toward revenue goals &amp; the leaderboard. Optional, you can add it later via Record response.</p>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button className="bg-forest hover:bg-forest-600 text-ivory" onClick={confirm} disabled={loading} data-testid="confirm-sent-submit">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Confirm sent"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
